import { union } from "lodash";
import { useContext, Component } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { ProjectPerspective } from "@app/projects";
import RegionStatusFilter from "@app/projects/network-assessment/RegionStatusFilter";
import ChangedGapsFilter from "@app/projects/network-assessment/ChangedGapsFilter";
import SpecialtyFilter from "@app/projects/network-assessment/SpecialtyFilter";
import { IconToggle, TooltipWrapper } from "@app/shared";

import { RegionSetStatusType } from "@app/projects/enumerators";
import { AdequacyQueryShape } from "@app/projects/CountyProjectContainer";
import { CountyProjectContext, CountyProjectContextProps } from "@app/projects";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { querystringify, parseQuerystring } from "@app/shared/utility";
import {
  getProjectPerspectiveDescription,
  getProjectPerspectiveFromFilterString
} from "@app/projects/projectPerspectiveConversions";
import DefaultViewFilter from "@app/shared/DefaultViewFilter";
import useCleanHistory, { CleanHistoryProps } from "@app/shared/useCleanHistory";

/** The set of possible toggles. */
type ToggleType = "all" | "gaps" | "exceptions" | "expansion" | "changedgaps";

interface DetailsFiltersProps extends RouteComponentProps {
  availableRegionFilters?: RegionSetStatusType[];
  disabled?: boolean;
  enableChangedGaps?: boolean;
  enableExceptions?: boolean;
  enableGaps?: boolean;
  showRegionStatusSelectors?: boolean;
}

type CombinedFiltersProps = DetailsFiltersProps & CountyProjectContextProps & CleanHistoryProps;

const DetailsFiltersContainer = (props: DetailsFiltersProps) => {
  const projectContext = useContext(CountyProjectContext);
  const pushOrReplaceLocation = useCleanHistory();
  return <DetailsFilters {...props} {...projectContext} pushOrReplaceLocation={pushOrReplaceLocation} />;
};

class DetailsFilters extends Component<CombinedFiltersProps> {
  componentDidUpdate(prevProps: CombinedFiltersProps) {
    if (prevProps.location.search === this.props.location.search && prevProps.perspective === this.props.perspective) {
      return;
    }
    const prevQuery = parseQuerystring(prevProps.location.search) as AdequacyQueryShape;
    const currQuery = parseQuerystring(this.props.location.search) as AdequacyQueryShape;

    const perspectiveChanged = prevProps.perspective !== this.props.perspective;

    if ((prevQuery.changedGaps !== currQuery.changedGaps || perspectiveChanged) && currQuery.changedGaps && (!this.props.compareToRunConfiguration || this.props.perspective !== ProjectPerspective.All)) {
      this.updateQuery("all", true);
    }

    if ((prevQuery.specialtyType !== currQuery.specialtyType || perspectiveChanged) && currQuery.specialtyType && this.props.perspective !== ProjectPerspective.All && this.props.perspective !== ProjectPerspective.BySpecialty) {
      this.props.history.replace({
        ...this.props.history.location,
        search: querystringify({ ...currQuery, specialtyType: null }),
      });
    }
  }

  public render() {
    if (this.props.regionFilters.county && this.props.regionFilters.specialty) {
      //don't allow filtering once the user is already viewing a region-set-specialty-group summary result
      return null;
    }
    const query = parseQuerystring(this.props.location.search) as AdequacyQueryShape;

    const availableRegionSetStatusTypes =
      this.props.adequacyResult
        ? this.props.compareToAdequacyResult
          ? union(this.props.adequacyResult.availableRegionSetStatusTypes, this.props.compareToAdequacyResult.availableRegionSetStatusTypes)
          : this.props.adequacyResult.availableRegionSetStatusTypes
        : this.props.availableRegionFilters || [];

    const isShowingAggregates = !this.props.regionFilters.county && !this.props.regionFilters.specialty && this.props.perspective !== ProjectPerspective.All;

    const gapsDisabled = !this.props.enableGaps &&
      (this.props.loading || this.props.loadingResults ||
        (!(this.props.adequacyFilters && this.props.adequacyFilters.gaps)
          && !(this.props.adequacyResult && this.props.adequacyResult.hasGaps)
          && !(this.props.compareToAdequacyResult && this.props.compareToAdequacyResult.hasGaps))) ||
      (this.props.cmsCreditShowOnlyChanges && this.props.cmsCreditIsApplying) ||
      (this.props.cmsCreditIsApplying && isShowingAggregates &&
        (this.props.adequacyResult.aggregateSummary.percentPassBoth === 100 || this.props.adequacyResult.aggregateSummary.percentPassBothCMSCredit === 100)) ||
      (this.props.cmsCreditIsApplying && !isShowingAggregates &&
        this.props.adequacyResult.details.length > 0 &&
        this.props.adequacyResult.details.every((d) => (d.applicationCreditMetCriteria || d.metCriteria)));

    const exceptionsDisabled = !this.props.enableExceptions && (this.props.loading || this.props.loadingResults || !(this.props.adequacyFilters && this.props.adequacyFilters.exceptions) && !(this.props.adequacyResult && this.props.adequacyResult.hasExceptions));
    const changedGapsDisabled = !this.props.enableChangedGaps;
    const specialtyFilterDisabled = this.props.perspective !== ProjectPerspective.All && this.props.perspective !== ProjectPerspective.BySpecialty;

    const allDisabled = (this.props.loading || this.props.loadingResults) && !this.props.project.isReadOnly;

    const perspectiveDropdownItems = this.props.availablePerspectives.map((perspective) => ({
      key: perspective,
      name: getProjectPerspectiveDescription(perspective),
    }));

    return (
      <div className="details-filters">
        <span className="pr10">
          {!this.props.regionFilters.county && perspectiveDropdownItems.length && (
            <NewActionMenu
              buttonText={`View by: ${perspectiveDropdownItems.find((item) => item.key === this.props.perspective).name}`}
              testId="details-filters__view-by-dropdown"
              buttonClassNames="qes-button-secondary"
              loading={this.props.disabled || this.props.loadingResults || this.props.loading}
              // force a rerender on a perspective change
              key={this.props.perspective}
            >
              {
                perspectiveDropdownItems.map((item) => {
                  const isSelected = item.key === this.props.perspective;
                  return (
                    <NewActionMenuItem
                      display={item.name}
                      isSelected={isSelected}
                      key={item.key}
                      onClick={() => this.setPerspective(item.key, true)}
                      testId={`details-filters__view-by-option--${item.key}`}
                    />
                  )
                })
              }
            </NewActionMenu>
          )}
          <DefaultViewFilter filterSuffix={""}
                             filterOptions={perspectiveDropdownItems.map(item => item.name)}
                             onChange={this.setPerspectiveFromFilter}/>
        </span>

        <div>
          <div className="adequacy-toggle-container">
            <IconToggle
              active={query.all === undefined ? true : query.all === "true"}
              disabled={allDisabled}
              onToggle={(active) => this.updateQuery("all", active)}
              activeIconClass={["fa", "fa-check-circle"]}
              inActiveIconClass={["fa", "fa-circle-thin"]}
              baseClassName={["icon-toggle-tooltip", "mb-0"]}
              data-testid="details-filter__details-filters-all-icon-toggle"
            >
              All
            </IconToggle>
            <IconToggle
              active={query.gaps === "true"}
              disabled={gapsDisabled || allDisabled}
              onToggle={(active) => this.updateQuery("gaps", active)}
              activeIconClass={["fa", "fa-check-circle"]}
              inActiveIconClass={["fa", "fa-circle-thin"]}
              baseClassName={["icon-toggle-tooltip", "mb-0"]}
              data-testid="details-filter__details-filters-gaps-icon-toggle"
            >
              {gapsDisabled
                ? (
                  <TooltipWrapper tooltipWidth="150px"
                                  text="This filter is disabled because there is no data to display for GAPS">
                    Gaps
                  </TooltipWrapper>
                )
                : (<>Gaps</>)
              }
            </IconToggle>
            <IconToggle
              active={query.exceptions === "true"}
              disabled={exceptionsDisabled || allDisabled}
              onToggle={(active) => this.updateQuery("exceptions", active)}
              activeIconClass={["fa", "fa-check-circle"]}
              inActiveIconClass={["fa", "fa-circle-thin"]}
              baseClassName={["icon-toggle-tooltip", "mb-0"]}
              data-testid="details-filter__details-filters-exceptions-icon-toggle"
            >
              {exceptionsDisabled
                ? (
                  <TooltipWrapper tooltipWidth="150px"
                                  text="This filter is disabled because there is no data to display for EXCEPTIONS">
                    Exceptions
                  </TooltipWrapper>
                )
                : (<>Exceptions</>)
              }
            </IconToggle>
          </div>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={changedGapsDisabled || allDisabled
              ? (
                <TooltipWrapper tooltipWidth="150px"
                                text={"This filter is only available when viewing by \"All\" and comparing results that have changed gaps"}>
                  Changed Gaps
                </TooltipWrapper>
              )
              : (<> Changed Gaps </>)
            }
            alignRight={true}
            testId="details-filters__county-changed-gaps-dropdown"
            disabled={changedGapsDisabled || allDisabled}
          >
            <ChangedGapsFilter/>
          </NewActionMenu>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={specialtyFilterDisabled || allDisabled
              ? (
                <TooltipWrapper tooltipWidth="150px"
                  text={"This filter is only available when viewing by \"All\" or viewing by \"Specialty\""}>
                  Specialty Filter
                </TooltipWrapper>
              )
              : (<> Specialty Filter </>)
            }
            alignRight={true}
            testId="details-filters__county-specialties"
            disabled={specialtyFilterDisabled || allDisabled}
          >
            <SpecialtyFilter />
          </NewActionMenu>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={<><i className="fa fa-filter"/> Status Filters </>}
            alignRight={true}
            testId="details-filters__county-status-dropdown"
            disabled={allDisabled || !!this.props.regionFilters.county}
          >
            <RegionStatusFilter availableRegionSetStatusTypes={availableRegionSetStatusTypes}
                                showRegionStatusSelectors={this.props.showRegionStatusSelectors}/>
          </NewActionMenu>
        </div>
      </div>
    );
  }

  private setPerspectiveFromFilter = (filter: string, forceReload: boolean) => {
    const perspective = getProjectPerspectiveFromFilterString(filter);
    this.setPerspective(perspective, forceReload);
  }

  private setPerspective = (perspective: ProjectPerspective, forceRefresh: boolean) => {
    this.props.setPerspective(perspective, forceRefresh);
    if (forceRefresh) {
      this.updatePerspectiveQueryParam(perspective);
    }
  }

  private updatePerspectiveQueryParam = (perspective: ProjectPerspective) => {
    const query = parseQuerystring(this.props.location.search);
    const newQuery = {
      ...query,
      viewBy: perspective,
    };
    const qs = querystringify(newQuery);
    this.props.pushOrReplaceLocation({
      search: qs
    });
  }

  private updateQuery = (toggleType: ToggleType, active: boolean) => {
    let query = parseQuerystring(this.props.location.search) as AdequacyQueryShape;

    switch (toggleType) {
      case "all":
        query = {
          ...query,
          all: active.toString(),
          changedGaps: null,
          exceptions: "false",
          gaps: "false",
        };
        break;
      case "gaps":
        query = {
          ...query,
          all: "false",
          changedGaps: null,
          exceptions: "false",
          gaps: active.toString(),
        };
        break;
      case "exceptions":
        query = {
          ...query,
          all: "false",
          changedGaps: null,
          exceptions: active.toString(),
          gaps: "false",
        };
        break;
      default:
        throw new Error(`Unknown toggle type "${toggleType}", could not update query string.`);
    }

    // If we end up in the "all false" state, just do a clean reset.
    if ((query.all === "false")
      && (query.exceptions === "false")
      && (query.gaps === "false")
      && (query.changedGaps === null)
      && (query.specialtyType === null)) {
      query.all = query.exceptions = query.gaps = undefined;
    }

    const qs = querystringify(query as { [key: string]: unknown });
    this.props.pushOrReplaceLocation({ search: qs });
  }
}

const WrappedDetailsFilters = withRouter(DetailsFiltersContainer);

export default WrappedDetailsFilters;
